import { useMemo } from 'react';

import { Flex, FloraButton } from '@grupoboticario/flora-react';
import { DollarSignCircleIcon, CalendarIcon } from '@grupoboticario/flora-react-icons';
import { Link } from 'react-router-dom';

import { ResponsiveFloraTypography, Tag } from 'components';
import { getOrderStatus, getShortCycle, getShortDate, getFormattedPrice } from 'helpers';
import { useOrders, useTracking } from 'hooks';

import Styles from './Card.styles';

import type { OrderType } from 'domains/order';

export const CardHistory = ({ order }: { order: OrderType }) => {
  const totalAmount = getFormattedPrice(order.amount);
  const currentCycle = getShortCycle(order.cycle);
  const tagStatus = getOrderStatus({ status: order.statusView, dateTime: order.deliveryDate });
  const orderDate = getShortDate(order.orderDate);
  const { sendDataToGTM } = useTracking();
  const { data } = useOrders();
  const ordersData = useMemo(() => (data ? data?.pages.flatMap((item) => item.data) : []), [data]);

  const handleTrack = () => {
    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'interaction_pedidos',
        cd_interaction_detail: 'acessar-detalhes-do-pedido',
        transaction_id: order.id,
        cd_status_pedido: tagStatus.tagText.toLowerCase(),
        cd_posicao_pedido: ordersData.findIndex((item) => item.id === order.id) + 1,
        cd_index_pagina:
          (data &&
            data.pages.find((page) => page.data.find((item) => item.id === order.id))?.pagination
              .currentPage) ||
          1,
      },
    });
  };

  return (
    <Styles.Card>
      <Styles.Heading align='flex-start' justify='space-between'>
        <ResponsiveFloraTypography
          fontSize='subtitle'
          color='$backgroundAltSecondary'
          fontWeight='bold'
          as='h2'
        >
          Pedido {order.id}
        </ResponsiveFloraTypography>
        <Tag data={tagStatus} type='pedido' />
      </Styles.Heading>
      {Boolean(tagStatus.subTitleText) ? (
        <Flex justify='flex-start'>
          <ResponsiveFloraTypography
            fontSize='exceptionsAuxiliar'
            color='$backgroundAltSecondary'
            css={{ textAlign: 'left' }}
          >
            {tagStatus.subTitleText}
          </ResponsiveFloraTypography>
        </Flex>
      ) : null}

      <Styles.Divider aria-hidden />

      <Styles.Value align='center' justify='space-between'>
        <Flex align='center' justify='flex-start' gap='$1'>
          <DollarSignCircleIcon />
          <ResponsiveFloraTypography>Valor total</ResponsiveFloraTypography>
        </Flex>
        <ResponsiveFloraTypography>{totalAmount}</ResponsiveFloraTypography>
      </Styles.Value>

      <Flex align='center' justify='space-between'>
        <Flex
          align='center'
          justify='flex-start'
          gap='$1'
          aria-label={`Pedido realizado em ${orderDate} no ciclo ${currentCycle}`}
        >
          <CalendarIcon />
          <ResponsiveFloraTypography>Realizado</ResponsiveFloraTypography>
        </Flex>
        <ResponsiveFloraTypography aria-hidden>
          {orderDate} - Ciclo {currentCycle}
        </ResponsiveFloraTypography>
      </Flex>

      <Styles.Divider aria-hidden />
      <FloraButton
        aria-label={`Acessar detalhes do pedido ${order.id}`}
        as={Link}
        data-id={order.id}
        isFull
        onClick={handleTrack}
        role='button'
        size='small'
        to={`/pedido/${order.id}`}
      >
        <ResponsiveFloraTypography fontSize='bodySmallShort' fontWeight='bold'>
          Acessar detalhes do pedido
        </ResponsiveFloraTypography>
      </FloraButton>
    </Styles.Card>
  );
};
